/**
 * MobileResearch — a browsable list of recent research items.
 *
 * CLUSTER-2 (research-visibility uplift). Desktop has a full research library; mobile previously had no
 * way to browse the ~1,900 research drafts at all — users had to use Ask to "find" anything. This screen
 * lists the most-recent items the caller is permitted to see, each tappable through to the full
 * MobileResearchDetail.
 *
 * Route: /partner/m/research
 *
 * DATA — `mobile_research_list()` SECURITY DEFINER RPC, which replicates the research_items RLS SELECT
 * predicate in-body so each caller sees only their permitted rows. It returns a JSON ARRAY directly:
 *   [{ id, title, status, primary_axis, conviction, time_horizon, updated_at, summary }, …]
 * (LIMIT 40, updated_at desc). A non-research caller is rejected by the gate (42501) and surfaces as a
 * hard error only when there's nothing cached to fall back to; an authorised caller with no items gets
 * `[]` — a valid, calm empty state.
 *
 * READ-ONLY and offline-aware, mirroring the sibling mobile pages exactly:
 *   • ONE useCachedQuery write-throughs the resolved array (queryFn THROWS on { error } so the cached
 *     last-known-good keeps serving offline rather than being overwritten with a partial/empty value).
 *   • A stable EMPTY array guarantees the screen is never blank on an offline cold start.
 *   • The "Saved <relative time>" header hint + amber-when-offline colour mirror the sibling pages.
 *
 * The generated supabase types are stale for this RPC, so the call is cast through `any` (matching the
 * other cluster screens). No writes. Web/desktop are untouched (this is a /partner/m/* surface).
 */
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useCachedQuery } from '@/lib/offline/cachedQuery';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { humanise } from '@/components/research-hub/labels';

// ---------------------------------------------------------------------------
// Shapes — exactly the keys mobile_research_list() builds.
// ---------------------------------------------------------------------------

interface ResearchListRow {
  id: string;
  title: string | null;
  status: string | null;
  primary_axis: string | null;
  conviction: number | null;
  time_horizon: string | null;
  updated_at: string | null;
  summary: string | null;
}

const EMPTY_LIST: ResearchListRow[] = [];

async function fetchResearchList(): Promise<ResearchListRow[]> {
  const { data, error } = await (supabase.rpc as any)('mobile_research_list');
  if (error) throw error;
  // The RPC returns a JSON array directly (COALESCE(jsonb_agg(...), '[]')).
  return Array.isArray(data) ? (data as ResearchListRow[]) : [];
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatRelative(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return null;
  }
}

// conviction is a 1..5 integer on the item (desktop renders "Conviction n/5").
function convictionLabel(c: number | null): string | null {
  if (typeof c !== 'number' || !Number.isFinite(c)) return null;
  return `Conviction ${c}/5`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function MobileResearch() {
  const online = useIsOnline();

  const list = useCachedQuery<ResearchListRow[]>({
    queryKey: ['mobile-research-list'],
    cacheKey: 'mobile.research_list',
    queryFn: fetchResearchList,
  });

  const items = list.data ?? EMPTY_LIST;

  const showStale = list.isFromCache && list.cachedAt != null;
  let staleLabel: string | null = null;
  if (showStale && list.cachedAt) {
    try {
      staleLabel = `Saved ${formatDistanceToNow(new Date(list.cachedAt), { addSuffix: true })}`;
    } catch {
      staleLabel = 'Showing saved data';
    }
  }

  // Hard error only when there's nothing cached to fall back to (e.g. signed-out 28000 or an
  // unauthorised 42501 on a cold start with no last-known-good).
  const hardError = list.isError && !list.isFromCache && list.data === undefined;

  return (
    <div className="min-h-screen bg-background px-4 pb-8 pt-[calc(1.25rem+env(safe-area-inset-top))]">
      <header className="mb-4">
        <Link
          to="/partner/m/home"
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          Home
        </Link>
        <h1 className="font-heading text-2xl font-semibold text-primary">Research</h1>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {items.length > 0 && (
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
              {items.length} recent item{items.length === 1 ? '' : 's'}
            </p>
          )}
          {showStale && staleLabel && (
            <p
              className={cn(
                'text-[11px] font-medium uppercase tracking-wide',
                online ? 'text-muted-foreground/70' : 'text-amber-600',
              )}
            >
              {staleLabel}
            </p>
          )}
        </div>
      </header>

      <div className="space-y-3">
        {hardError ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">Couldn’t load</p>
            <p className="mt-2 text-sm text-muted-foreground">
              {online
                ? 'We couldn’t load research just now. Pull back in a moment.'
                : 'You’re offline and there’s no saved research yet. This will fill in once you reconnect.'}
            </p>
          </section>
        ) : items.length === 0 ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <FileText className="h-5 w-5" aria-hidden="true" />
              </span>
              <h2 className="font-heading text-sm font-semibold">Nothing to show yet</h2>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Research items you can see will appear here as they’re published.
            </p>
            {!online && (
              <p className="mt-2 text-[11px] uppercase tracking-wide text-amber-600">
                You’re offline — this will refresh once you reconnect.
              </p>
            )}
          </section>
        ) : (
          <ul className="space-y-3">
            {items.map((item) => {
              const when = formatRelative(item.updated_at);
              const conviction = convictionLabel(item.conviction);
              const axis = item.primary_axis ? humanise(item.primary_axis) : null;
              return (
                <li key={item.id}>
                  <Link
                    to={`/partner/m/research/${encodeURIComponent(item.id)}`}
                    className="block rounded-xl border border-border bg-card p-4 transition-colors active:bg-muted/50"
                    aria-label={`Open research item: ${item.title ?? 'Untitled'}`}
                  >
                    <div className="flex items-start gap-2">
                      <h2 className="min-w-0 flex-1 font-heading text-sm font-semibold">
                        {item.title ?? 'Untitled'}
                      </h2>
                      <ChevronRight
                        className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/50"
                        aria-hidden="true"
                      />
                    </div>

                    {/* Axis + conviction badges — the firm's quick-read signal on the item. */}
                    {(axis || conviction) && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {axis && (
                          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                            {axis}
                          </span>
                        )}
                        {conviction && (
                          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                            {conviction}
                          </span>
                        )}
                      </div>
                    )}

                    {item.summary && (
                      <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{item.summary}</p>
                    )}

                    {when && (
                      <p className="mt-2 text-[11px] uppercase tracking-wide text-muted-foreground/70">
                        Updated {when}
                      </p>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
